import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { pageKey } from '@dashboard/lib/queryKeys';
import usePage from '@dashboard/hooks/usePage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import { useTicketGroups } from '../TicketGroupsContext';
import { fromZonedTime } from 'date-fns-tz';
import { formatDateForInput } from '@/dashboard/services/formatting/date';
import handleAvailabilityBehaviorOnChange from '../handleAvailabilityBehaviorOnChange';
import {
  TicketGroupAvailabilityBehavior,
  TicketGroupAvailabilityStatus,
} from '@/dashboard/enums/tickets';
import * as Yup from 'yup';

const AddTicketGroupDialog = () => {
  const { sellerId, token } = useAuth();
  const { page } = usePage();
  const queryClient = useQueryClient();
  const { state: ticketGroups } = useTicketGroups();

  const totalQuantitySold = 0;
  const defaultAvailabilityBehavior = TicketGroupAvailabilityBehavior.ALWAYS_AVAILABLE;

  const {
    isAddingTicketGroup,
    setIsAddingTicketGroup,
    setIsEditingTicketGroup,
    setIsAddingTicketType,
  } = useTicketGroupEdit();

  const getSaleStartOnFromReference = triggerTicketGroup => {
    if (triggerTicketGroup == null || page?.eventDetails?.timezone == null) return null;

    const referencedTicket = ticketGroups.find(t => t._id === triggerTicketGroup);

    if (
      referencedTicket?.availabilityBehavior === TicketGroupAvailabilityBehavior.UNTIL_EVENT_STARTS
    ) {
      if (page?.eventDetails?.startDate != null) {
        return formatDateForInput(page.eventDetails.startDate, page?.eventDetails?.timezone);
      }

      return null;
    }

    if (referencedTicket?.saleEndOn != null) {
      return formatDateForInput(referencedTicket.saleEndOn, page?.eventDetails?.timezone);
    }

    return null;
  };

  const addTicketGroupMutation = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${sellerId}/ticket-groups`, token, {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          pageId: page._id,
        }),
      }),
    onSuccess: async data => {
      setIsAddingTicketGroup(false);

      await queryClient.invalidateQueries({ queryKey: pageKey(page._id) });

      setIsEditingTicketGroup(true, data._id);
      setIsAddingTicketType(true);
    },
  });

  function closeDialog() {
    setIsAddingTicketGroup(false);
  }

  return (
    <Dialog
      title="Add ticket group"
      cancelButton={null}
      confirmButton={null}
      open={isAddingTicketGroup}
      onCancel={closeDialog}
      size="lg"
    >
      <Form
        validationSchema={Yup.object().shape({
          triggerTicketGroup: Yup.string().when('availabilityBehavior', {
            is: value =>
              [
                TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
                TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
              ].includes(value),
            then: Yup.string().nullable().required('Please select a ticket group'),
            otherwise: Yup.string().nullable(),
          }),
          saleStartOn: Yup.string().when('availabilityBehavior', {
            is: TicketGroupAvailabilityBehavior.DATE_TIME_BASED,
            then: Yup.string()
              .nullable()
              .test('date-required', 'A start or end date is required.', function (value) {
                const { saleEndOn } = this.parent;
                // Only show error on saleStartOn if both are empty
                if ((!value || value === '') && (!saleEndOn || saleEndOn === '')) {
                  return false;
                }
                return true;
              }),
            otherwise: Yup.string().nullable(),
          }),
          saleEndOn: Yup.string().when('availabilityBehavior', {
            is: TicketGroupAvailabilityBehavior.DATE_TIME_BASED,
            then: Yup.string().nullable(),
            otherwise: Yup.string().nullable(),
          }),
        })}
        initialValues={{
          name: '',
          description: '',
          preselect: {
            enabled: false,
            ticketType: null,
            quantity: null,
          },
          ticketSelectionType: 'quantity',
          capacity: null,
          hidden: false,
          reference: '',
          availabilityStatus: TicketGroupAvailabilityStatus.AVAILABLE,
          saleStartOn: null,
          saleEndOn: null,
          triggerTicketGroup: null,
          availabilityBehavior: defaultAvailabilityBehavior,
        }}
        inputClassName="text-sm"
        fields={({ values, setFieldValue }) => {
          const availableTicketGroupOptions = ticketGroups.map(t => ({
            label: t.name,
            value: t._id,
          }));

          const isCapacityReached =
            (values.capacity != null &&
              values.capacity != '' &&
              totalQuantitySold >= values.capacity) ||
            values.capacity === 0;

          return [
            {
              name: 'name',
              label: 'Group name',
              type: 'text',
              required: true,
              autoFocus: true,
              autoComplete: 'off',
              reduceMargin: true,
              gap: 'sm',
            },
            {
              name: 'description',
              label: 'Group description',
              type: 'text',
              reduceMargin: true,
              gap: 'sm',
            },
            {
              type: 'columns',
              columns: 2,
              fields: [
                {
                  name: 'availabilityStatus',
                  type: 'select',
                  label: 'Availability',
                  tooltip:
                    isCapacityReached &&
                    `Disabled when tickets sold (currently ${totalQuantitySold} sold) exceeds group availability. Increase availability to change group availability status.`,
                  options: [
                    {
                      label: 'Select a group availability',
                      value: '',
                      disabled: true,
                    },
                    {
                      label: 'On sale',
                      value: TicketGroupAvailabilityStatus.AVAILABLE,
                      disabled: isCapacityReached,
                    },
                    {
                      label: 'Unavailable',
                      value: TicketGroupAvailabilityStatus.UNAVAILABLE,
                      disabled: isCapacityReached,
                    },
                    {
                      label: 'Sold out',
                      value: TicketGroupAvailabilityStatus.SOLD_OUT,
                    },
                  ],
                },
                {
                  name: 'capacity',
                  label: 'Quantity available',
                  placeholder: 'Quantity available',
                  type: 'number',
                  onChange: e => {
                    setFieldValue('capacity', e.target?.value);
                    const capacity = parseInt(e.target?.value, 10);

                    if (
                      capacity === 0 ||
                      (!isNaN(capacity) && totalQuantitySold >= e.target?.value)
                    ) {
                      setFieldValue('availabilityStatus', 'sold_out');
                    }
                  },
                },
              ],
              reduceMargin: true,
              gap: 'sm',
            },
            {
              name: 'availabilityBehavior',
              label: 'Available from',
              type: 'select',
              options: [
                {
                  label: 'Now until event starts',
                  value: TicketGroupAvailabilityBehavior.UNTIL_EVENT_STARTS,
                },
                {
                  label: 'Always',
                  value: TicketGroupAvailabilityBehavior.ALWAYS_AVAILABLE,
                },
                {
                  label: 'Date & time',
                  value: TicketGroupAvailabilityBehavior.DATE_TIME_BASED,
                },
                {
                  label: 'When another ticket group sale ends',
                  value: TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                },
                {
                  label: 'When another ticket group sells out',
                  value: TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
                },
                {
                  label: 'When another ticket group sells out or sale ends',
                  value: TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                },
              ],
              reduceMargin: true,
              gap: 'sm',
              onChange: e => {
                handleAvailabilityBehaviorOnChange({
                  triggerKey: 'triggerTicketGroup',
                  value: e.target?.value,
                  setFieldValue,
                  page,
                  values,
                  getSaleStartOnFromReference,
                });
              },
            },
            {
              name: 'triggerTicketGroup',
              type: 'select',
              label: 'Ticket group',
              options: [
                {
                  label: 'Select a ticket group',
                  value: '',
                },
                ...availableTicketGroupOptions,
              ],
              visible: values =>
                [
                  TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                  TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                  TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
                ].includes(values.availabilityBehavior),
              reduceMargin: true,
              gap: 'sm',
              onChange: e => {
                if (e.target?.value) {
                  const saleStartOn = getSaleStartOnFromReference(e.target?.value);
                  if (saleStartOn) {
                    setFieldValue('saleStartOn', saleStartOn);
                  } else {
                    setFieldValue('saleStartOn', '');
                  }
                } else if (e.target?.value == '') {
                  setFieldValue('saleStartOn', '');
                  setFieldValue('saleEndOn', '');
                }

                setFieldValue('triggerTicketGroup', e.target.value);
              },
            },
            {
              type: 'columns',
              columns: 2,
              fields: [
                {
                  name: 'saleStartOn',
                  label: 'Start sale on',
                  placeholder: 'Start sale on',
                  type: 'datetime-local',
                  noMargin: true,
                  disabled:
                    values.availabilityBehavior !== TicketGroupAvailabilityBehavior.DATE_TIME_BASED,
                },
                {
                  name: 'saleEndOn',
                  label: 'End sale on',
                  placeholder: 'End sale on',
                  type: 'datetime-local',
                  noMargin: true,
                },
              ],
              visible: values => {
                if (
                  values.availabilityBehavior === TicketGroupAvailabilityBehavior.DATE_TIME_BASED
                ) {
                  return true;
                }

                return (
                  [
                    TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                    TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
                  ].includes(values.availabilityBehavior) &&
                  (values.triggerTicketGroup != null || values.triggerTicketGroup != '')
                );
              },
              reduceMargin: true,
              gap: 'sm',
            },
            {
              name: 'saleEndOn',
              label: 'End sale on',
              placeholder: 'End sale on',
              type: 'datetime-local',
              reduceMargin: true,
              visible: values =>
                values.availabilityBehavior ===
                  TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT &&
                (values.triggerTicketGroup != null || values.triggerTicketGroup != ''),
            },
            {
              type: 'fieldgroup',
              label: 'Visibility',
              fields: [
                {
                  label: 'Hide ticket',
                  name: 'hidden',
                  type: 'checkbox',
                  tooltip: 'Hide this ticket group and display it via query parameters',
                },
                {
                  label: 'Hide when scheduled',
                  name: 'hideWhenScheduled',
                  type: 'checkbox',
                  tooltip: 'Hide this ticket group when it is scheduled to go on sale',
                },
                {
                  label: 'Hide when not on sale',
                  name: 'hideWhenNotOnSale',
                  type: 'checkbox',
                  tooltip:
                    'Hide this ticket group outside its sales dates (before start or after end date)',
                },
                {
                  label: 'Hide when unavailable',
                  name: 'hideWhenUnavailable',
                  type: 'checkbox',
                  tooltip: 'Hide this ticket group when it is unavailable',
                },
                {
                  label: 'Hide when sold out',
                  name: 'hideWhenSoldOut',
                  type: 'checkbox',
                  tooltip: 'Hide this ticket group when it is sold out',
                },
              ],
              reduceMargin: true,
            },
            {
              name: 'ticketSelectionType',
              label: 'Ticket selection',
              type: 'select',
              options: [
                { label: 'Quantity select', value: 'quantity' },
                { label: 'Multiple select', value: 'multiple' },
                { label: 'Single select', value: 'single' },
              ],
              reduceMargin: true,
            },
            {
              name: 'preselect.enabled',
              label: 'Preselect ticket',
              type: 'checkbox',
              reduceMargin: true,
            },
            {
              type: 'columns',
              columns: values.ticketSelectionType === 'quantity' ? 2 : 1,
              noMargin: true,
              gap: 'sm',
              fields: [
                {
                  name: 'preselect.ticketType',
                  label: 'Select ticket',
                  type: 'select',
                  options: [
                    {
                      label: 'Select ticket',
                      value: '',
                      default: true,
                    },
                  ],
                  visible: values => values.preselect.enabled,
                  reduceMargin: true,
                },
                {
                  name: 'preselect.quantity',
                  label: 'Preselect quantity',
                  placeholder: 'Quantity',
                  type: 'number',
                  reduceMargin: true,
                  visible: values =>
                    values.preselect.enabled && values.ticketSelectionType === 'quantity',
                },
              ],
            },
            {
              name: 'reference',
              label: 'Reference',
              type: 'text',
              tooltip:
                'Used to identify this ticket group in Zapier, webhooks, and query parameters.',
            },
          ];
        }}
        onSubmit={async values => {
          try {
            if (
              values.availabilityBehavior === TicketGroupAvailabilityBehavior.DATE_TIME_BASED &&
              (values.saleStartOn == null || values.saleStartOn === '') &&
              (values.saleEndOn == null || values.saleEndOn === '')
            ) {
              values.availabilityBehavior = TicketGroupAvailabilityBehavior.ALWAYS_AVAILABLE;
            }

            if (
              [
                TicketGroupAvailabilityBehavior.AFTER_TICKET_SALE_ENDS,
                TicketGroupAvailabilityBehavior.AFTER_TICKET_SOLD_OUT,
                TicketGroupAvailabilityBehavior.AFTER_TICKET_ENDS_OR_SOLD_OUT,
              ].includes(values.availabilityBehavior) &&
              (values.triggerTicketGroup == null || values.triggerTicketGroup === '')
            ) {
              values.saleEndOn = null;
              values.saleStartOn = null;
              values.availabilityBehavior = TicketGroupAvailabilityBehavior.ALWAYS_AVAILABLE;
            }

            const data = await addTicketGroupMutation.mutateAsync({
              ...values,
              triggerTicketGroup:
                values.triggerTicketGroup === '' ? null : values.triggerTicketGroup,
              saleStartOn: values.saleStartOn
                ? fromZonedTime(values.saleStartOn, page?.eventDetails?.timezone)
                : null,
              saleEndOn: values.saleEndOn
                ? fromZonedTime(values.saleEndOn, page?.eventDetails?.timezone)
                : null,
            });

            closeDialog();

            return {
              data,
            };
          } catch (error) {
            return {
              error,
            };
          }
        }}
        formSubmitAlign="end"
        submitButton={{
          label: 'Save',
        }}
      />
    </Dialog>
  );
};

export default AddTicketGroupDialog;
